import React, { useState } from "react";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";

import styles from "./RegistrationPage.module.css";
import { db } from "../../services/firebaseConfig";
import Button from "../../components/common/Button";
import { Input } from "../../components/common/Input";
import InputArea from "../../components/common/InputArea";
import About from "../../components/About/About";

export default function RegistrationPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    profession: "",
    bio: "",
    linkedin: "",
    message: "",
    website: "",
  });
  const [status, setStatus] = useState(null);

  const onChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const validate = () => {
    if (!form.name.trim() || !form.email.trim() || !form.profession.trim())
      return "Preencha nome, email e profissão.";
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!re.test(form.email)) return "Email inválido.";
    if (form.website) return "Spam detectado.";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      setStatus({ kind: "error", text: err });
      return;
    }
    setStatus({ kind: "saving", text: "Enviando..." });

    try {
      await addDoc(collection(db, "registrations"), {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        profession: form.profession.trim(),
        bio: form.bio.trim(),
        linkedin: form.linkedin.trim(),
        message: form.message.trim(),
        status: "pre-registration",
        source: "pbp-intro-cycle",
        createdAt: serverTimestamp(),
      });

      setStatus({
        kind: "success",
        text: "Pré-inscrição recebida. Você receberá por e-mail as orientações sobre os Encontros Introdutórios e as próximas etapas do processo.",
      });

      setForm({
        name: "",
        email: "",
        profession: "",
        bio: "",
        linkedin: "",
        message: "",
        website: "",
      });
    } catch (error) {
      console.error("Registration error", error);
      setStatus({
        kind: "error",
        text: "Erro ao enviar. Tente novamente.",
      });
    }
  };

  return (
    <section className={styles.page}>
      <div className={styles.wrapper}>
        {/* ===== CABEÇALHO ===== */}
        <header className={styles.header}>
          <h1>Grupo de Discussão em Terapia de Aceitação e Compromisso</h1>
          <p className={styles.lead}>
            Pré-inscrição para participação no grupo regular, com acesso aos
            Encontros Introdutórios.
          </p>
        </header>

        {/* ===== BLOCO EXPLICATIVO ===== */}
        <section className={styles.infoBox}>
          <h2>O que é este espaço</h2>
          <p>
            O Grupo de Discussão em Terapia de Aceitação e Compromisso é um
            espaço educacional voltado a psicólogos e médicos interessados em
            ACT, RFT e terapias contextuais, com foco em leitura funcional de
            casos clínicos.
          </p>

          <p>
            Este não é um grupo aberto. A entrada ocorre por meio de um processo
            de alinhamento conceitual e curadoria.
          </p>

          <h2>Como o trabalho clínico é sustentado</h2>
          <p>
            Além dos encontros ao vivo, o grupo se apoia em um conjunto de
            ferramentas desenvolvidas para sustentar e aprofundar o raciocínio
            clínico baseado em processos ao longo do tempo.
          </p>
          <p>
            Essas ferramentas não substituem o encontro clínico nem o julgamento
            profissional. Elas funcionam como suportes para reflexão, formulação
            e discussão de casos dentro e fora dos encontros.
          </p>

          <ul>
            <li>
              <strong>Formulação funcional baseada em processos</strong>
              <br />
              Ferramenta para externalização e organização de hipóteses
              clínicas, integrando história, contexto, processos psicológicos e
              valores.
            </li>
            <li>
              <strong>Escalas de autoobservação</strong>
              <br />
              Instrumentos como AAQ-II, DASS-21 e VLQ, pensados para uso
              reflexivo com pacientes e sempre integrados à formulação clínica.
            </li>
            <li>
              <strong>Metáforas clínicas em formato visual</strong>
              <br />
              Conjunto de metáforas baseadas em ACT, utilizadas como recurso
              clínico e psicoeducacional, com indicações, limites e cuidados
              explicitados.
            </li>
          </ul>

          <h2>Encontros Introdutórios</h2>
          <p>
            Como parte do processo de entrada, serão realizados dois{" "}
            <strong>
              Encontros Introdutórios ao Grupo de Discussão em Terapia Baseada
              em Processos
            </strong>{" "}
            de forma gratuita.
          </p>

          <ul>
            <li>Datas: 28/01 e 11/02</li>
            <li>Formato: online, ao vivo (Google Meet)</li>
            <li>Horário: quarta-feira, às 19h</li>
          </ul>

          <p>
            Esses encontros têm caráter introdutório e formativo, com foco em
            apresentar a proposta do grupo, o modo de pensar clínico baseado em
            processos e o funcionamento do espaço regular.
          </p>

          <h2>Importante</h2>
          <ul>
            <li>Os Encontros Introdutórios não configuram o grupo regular</li>
            <li>A participação não garante vaga no grupo</li>
            <li>Não se trata de psicoterapia ou supervisão clínica formal</li>
          </ul>

          <p className={styles.disclaimer}>
            A responsabilidade ética e legal pelo atendimento clínico permanece
            integralmente com cada profissional participante.
          </p>

          <h2>Grupo regular</h2>
          <p>
            O grupo regular terá início em <strong>04/03</strong>, com encontros
            quinzenais e contribuição de <strong>R$ 50,00</strong> por mês. A
            duração será de 3 meses.
          </p>

          <p>
            As vagas são limitadas. A lista de prioridade será formada a partir
            da participação nos Encontros Introdutórios, considerando a ordem de
            inscrição e o alinhamento com a proposta do grupo.
          </p>

          <h2>Conduzido por Thiago Rocha</h2>
          <p>
            Thiago Rocha é psiquiatra, mestre em Ciências do Comportamento pela
            Universidade de Brasília (UnB), preceptor do programa de residência
            médica em psiquiatria da Secretaria de Saúde do Distrito Federal.
            Possui residência médica em psiquiatria realizada no Hospital de
            Base do Distrito Federal e é graduado em Medicina pela Escola
            Superior de Ciências da Saúde (ESCS).
          </p>
          <p>
            É responsável pela criação e supervisão do ambulatório de
            transtornos de personalidade grave e do ambulatório de psicoterapias
            da SESDF, que acompanha pacientes com transtornos de ansiedade e
            transtorno obsessivo-compulsivo (TOC) graves.
          </p>
          <p>
            Atuou como professor na Faculdade de Medicina da UnB e, atualmente,
            faz parte da edição da revista Journal of Psychiatry, Mental Health
            and Humanities. Dedica-se ao atendimento clínico, com foco nas
            terapias comportamentais contextuais, como a Terapia de Aceitação e
            Compromisso (ACT) e a Psicoterapia Analítica Funcional (FAP). Seu
            trabalho integra conhecimentos da psiquiatria e da psicologia
            contextual.
          </p>
        </section>

        {/* ===== FORMULÁRIO ===== */}
        <section className={styles.formBox}>
          <h2>Pré-inscrição</h2>

          <form onSubmit={handleSubmit} style={{ display: "grid", gap: 12 }}>
            <Input
              name="name"
              label="Nome completo"
              value={form.name}
              onChange={onChange}
            />
            <Input
              name="email"
              label="Email profissional"
              value={form.email}
              onChange={onChange}
            />
            <Input
              name="profession"
              label="Profissão / Formação"
              value={form.profession}
              onChange={onChange}
            />
            <Input
              name="linkedin"
              label="LinkedIn (opcional)"
              value={form.linkedin}
              onChange={onChange}
            />
            <InputArea
              name="bio"
              label="Breve descrição sobre sua prática clínica ou interesse em ACT"
              value={form.bio}
              onChange={onChange}
            />
            <InputArea
              name="message"
              label="Por que você tem interesse em participar do grupo?"
              value={form.message}
              onChange={onChange}
            />

            <input
              name="website"
              value={form.website}
              onChange={onChange}
              style={{ display: "none" }}
              tabIndex="-1"
              autoComplete="off"
            />

            <Button type="submit">Enviar pré-inscrição</Button>
          </form>

          {status && (
            <div
              style={{
                marginTop: 12,
                color: status.kind === "error" ? "crimson" : "green",
              }}
            >
              {status.text}
            </div>
          )}
        </section>
      </div>
    </section>
  );
}
